import React, { useEffect, useState } from 'react';
import { DollarSignIcon, PlusIcon, FilterIcon, BookIcon, TagIcon, SettingsIcon, AlertTriangleIcon } from 'lucide-react';
import { BudgetList } from './BudgetList';
import { BudgetForm } from './BudgetForm';
import { BudgetTypes } from './BudgetTypes';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
export type BudgetType = {
  id: string;
  name: string;
  description: string;
};
export type Budget = {
  id: string;
  name: string;
  description: string;
  budgetTypeId: string;
  budgetTypeName: string;
  glCode: string;
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  totalAmount: number;
  remainingAmount: number;
  currency: string;
  startDate: string;
  endDate: string;
  status: 'Active' | 'Archived';
  rules: string;
  createdAt: string;
  updatedAt: string;
};
export function BudgetManagement({
  locale = 'en-US'
}) {
  const [activeView, setActiveView] = useState<'list' | 'create' | 'edit' | 'types'>('list');
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [budgetTypes, setBudgetTypes] = useState<BudgetType[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    budgetType: '',
    orgUnit: '',
    glCode: '',
    status: ''
  });
  // Load sample data
  useEffect(() => {
    const sampleBudgetTypes: BudgetType[] = [{
      id: 'type-001',
      name: 'Operational',
      description: 'Regular operational expenses'
    }, {
      id: 'type-002',
      name: 'Project',
      description: 'Project-specific budgets'
    }, {
      id: 'type-003',
      name: 'Capital',
      description: 'Capital expenditure budgets'
    }, {
      id: 'type-004',
      name: 'Marketing',
      description: 'Marketing and advertising budgets'
    }];
    const sampleBudgets: Budget[] = [{
      id: 'budget-001',
      name: 'Office Supplies Q1 2023',
      description: 'Budget for office supplies for Q1 2023',
      budgetTypeId: 'type-001',
      budgetTypeName: 'Operational',
      glCode: '6100.100',
      scope: 'Global',
      totalAmount: 50000,
      remainingAmount: 32450,
      currency: 'USD',
      startDate: '2023-01-01',
      endDate: '2023-03-31',
      status: 'Active',
      rules: JSON.stringify({
        maxPerTransaction: 5000,
        warningThreshold: 0.8,
        vendorBlacklist: ['Vendor X', 'Vendor Y']
      }),
      createdAt: '2022-12-15T10:00:00Z',
      updatedAt: '2023-01-10T14:30:00Z'
    }, {
      id: 'budget-002',
      name: 'IT Equipment 2023',
      description: 'Annual budget for IT equipment purchases',
      budgetTypeId: 'type-001',
      budgetTypeName: 'Operational',
      glCode: '6200.200',
      scope: 'Global',
      totalAmount: 120000,
      remainingAmount: 85600,
      currency: 'USD',
      startDate: '2023-01-01',
      endDate: '2023-12-31',
      status: 'Active',
      rules: JSON.stringify({
        maxPerTransaction: 10000,
        approvalRequired: true,
        warningThreshold: 0.7
      }),
      createdAt: '2022-12-01T09:00:00Z',
      updatedAt: '2023-02-15T11:20:00Z'
    }, {
      id: 'budget-003',
      name: 'Singapore Office Marketing',
      description: 'Marketing budget for Singapore office',
      budgetTypeId: 'type-004',
      budgetTypeName: 'Marketing',
      glCode: '6400.100',
      scope: 'OrgUnit',
      orgUnitId: 'ou-apac-sg',
      orgUnitName: 'Singapore Office',
      orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
      totalAmount: 60000,
      remainingAmount: 45000,
      currency: 'SGD',
      startDate: '2023-01-01',
      endDate: '2023-12-31',
      status: 'Active',
      rules: JSON.stringify({
        campaignCode: 'SG-MKT-2023',
        maxPerTransaction: 8000
      }),
      createdAt: '2023-01-05T10:30:00Z',
      updatedAt: '2023-03-15T14:20:00Z'
    }, {
      id: 'budget-004',
      name: 'Mexico Office Renovation',
      description: 'Office renovation project budget for Mexico City',
      budgetTypeId: 'type-003',
      budgetTypeName: 'Capital',
      glCode: '1500.100',
      scope: 'OrgUnit',
      orgUnitId: 'ou-latam-mx',
      orgUnitName: 'Mexico Office',
      orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
      totalAmount: 150000,
      remainingAmount: 75000,
      currency: 'MXN',
      startDate: '2023-02-01',
      endDate: '2023-12-31',
      status: 'Active',
      rules: JSON.stringify({
        projectCode: 'MX-RENO-2023',
        maxPerTransaction: 30000,
        approvalRequired: true
      }),
      createdAt: '2023-01-20T08:15:00Z',
      updatedAt: '2023-04-10T11:45:00Z'
    }];
    setBudgetTypes(sampleBudgetTypes);
    setBudgets(sampleBudgets);
  }, []);
  const handleCreateBudget = (budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newBudget: Budget = {
      ...budget,
      id: `budget-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setBudgets([...budgets, newBudget]);
    setActiveView('list');
  };
  const handleUpdateBudget = (updatedBudget: Budget) => {
    setBudgets(budgets.map(budget => budget.id === updatedBudget.id ? {
      ...updatedBudget,
      updatedAt: new Date().toISOString()
    } : budget));
    setActiveView('list');
    setSelectedBudget(null);
  };
  const handleArchiveBudget = (budgetId: string) => {
    setBudgets(budgets.map(budget => budget.id === budgetId ? {
      ...budget,
      status: 'Archived' as const,
      updatedAt: new Date().toISOString()
    } : budget));
  };
  const handleRestoreBudget = (budgetId: string) => {
    setBudgets(budgets.map(budget => budget.id === budgetId ? {
      ...budget,
      status: 'Active' as const,
      updatedAt: new Date().toISOString()
    } : budget));
  };
  const handleDeleteBudget = (budgetId: string) => {
    setDeletingId(budgetId);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setBudgets(budgets.filter(b => b.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  const handleCreateBudgetType = (budgetType: Omit<BudgetType, 'id'>) => {
    const newBudgetType: BudgetType = {
      ...budgetType,
      id: `type-${Date.now()}`
    };
    setBudgetTypes([...budgetTypes, newBudgetType]);
  };
  const handleUpdateBudgetType = (updatedBudgetType: BudgetType) => {
    setBudgetTypes(budgetTypes.map(budgetType => budgetType.id === updatedBudgetType.id ? updatedBudgetType : budgetType));
  };
  const handleDeleteBudgetType = (budgetTypeId: string) => {
    const budgetsUsingType = budgets.some(budget => budget.budgetTypeId === budgetTypeId);
    if (budgetsUsingType) {
      alert('Cannot delete budget type that is in use by existing budgets.');
      return;
    }
    setBudgetTypes(budgetTypes.filter(budgetType => budgetType.id !== budgetTypeId));
  };
  const handleEditBudget = (budget: Budget) => {
    setSelectedBudget(budget);
    setActiveView('edit');
  };
  const handleFilterChange = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const filteredBudgets = budgets.filter(budget => {
    return (filters.budgetType === '' || budget.budgetTypeId === filters.budgetType) && (filters.orgUnit === '' || budget.orgUnitId === filters.orgUnit) && (filters.glCode === '' || budget.glCode.toLowerCase().includes(filters.glCode.toLowerCase())) && (filters.status === '' || budget.status === filters.status);
  });
  const globalBudgets = filteredBudgets.filter(b => b.scope === 'Global');
  const orgUnitBudgets = filteredBudgets.filter(b => b.scope === 'OrgUnit');
  return <div className="space-y-6" data-id="element-7283">
      <div className="mb-8" data-id="element-7284">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-7285">Budget Management</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-7286">
          Create and manage budgets at global and organisation unit levels
        </p>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-7287">
        <div className="border-b border-gray-200" data-id="element-7288">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-7289">
            <button onClick={() => setActiveView('list')} className={`${activeView === 'list' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-7290">
              <DollarSignIcon className="h-4 w-4 inline mr-2" data-id="element-7291" />
              Budgets
            </button>
            <button onClick={() => setActiveView('types')} className={`${activeView === 'types' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-7292">
              <TagIcon className="h-4 w-4 inline mr-2" data-id="element-7293" />
              Budget Types
            </button>
          </nav>
        </div>

        <div className="p-6" data-id="element-7294">
          {activeView === 'list' && <div className="space-y-6" data-id="element-7295">
              {/* Filters */}
              <div className="flex justify-between mb-6" data-id="element-7296">
                <div className="flex items-center space-x-4" data-id="element-7297">
                  <div className="relative" data-id="element-7298">
                    <select id="budgetType" name="budgetType" className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.budgetType} onChange={e => handleFilterChange('budgetType', e.target.value)} data-id="element-7299">
                      <option value="" data-id="element-7300">All Budget Types</option>
                      {budgetTypes.map(type => <option key={type.id} value={type.id} data-id="element-7301">
                          {type.name}
                        </option>)}
                    </select>
                  </div>
                  <div className="w-64" data-id="element-7302">
                    <OrgUnitSelector value={filters.orgUnit} onChange={id => handleFilterChange('orgUnit', id)} label="" placeholder="All Organisation Units" showPath={false} data-id="element-7303" />
                  </div>
                  <div className="relative" data-id="element-7304">
                    <select id="status" name="status" className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} data-id="element-7305">
                      <option value="" data-id="element-7306">All Statuses</option>
                      <option value="Active" data-id="element-7307">Active</option>
                      <option value="Archived" data-id="element-7308">Archived</option>
                    </select>
                  </div>
                  <div className="relative" data-id="element-7309">
                    <input type="text" name="glCode" id="glCode" placeholder="Filter by GL Code" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-48 sm:text-sm border-gray-300 rounded-md" value={filters.glCode} onChange={e => handleFilterChange('glCode', e.target.value)} data-id="element-7310" />
                  </div>
                </div>
                <button type="button" onClick={() => setActiveView('create')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7311">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-7312" />
                  Create Budget
                </button>
              </div>

              {/* Global Budgets */}
              <div className="bg-white shadow rounded-lg" data-id="element-7313">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-7314">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7315">
                    Global Budgets
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-7316">
                    Global budgets available to all organisation units
                  </p>
                </div>
                {globalBudgets.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-7317">
                    <p className="text-sm text-gray-500" data-id="element-7318">
                      No global budgets found.
                    </p>
                  </div> : <BudgetList budgets={globalBudgets} onEdit={handleEditBudget} onArchive={handleArchiveBudget} onRestore={handleRestoreBudget} onDelete={handleDeleteBudget} locale={locale} data-id="element-7319" />}
              </div>

              {/* OrgUnit Budgets */}
              <div className="bg-white shadow rounded-lg" data-id="element-7320">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-7321">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7322">
                    Organisation Unit Budgets
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-7323">
                    Budgets specific to individual organisation units
                  </p>
                </div>
                {orgUnitBudgets.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-7324">
                    <p className="text-sm text-gray-500" data-id="element-7325">
                      No organisation unit budgets found.
                    </p>
                  </div> : <BudgetList budgets={orgUnitBudgets} onEdit={handleEditBudget} onArchive={handleArchiveBudget} onRestore={handleRestoreBudget} onDelete={handleDeleteBudget} locale={locale} showOrgUnit={true} data-id="element-7326" />}
              </div>
            </div>}

          {activeView === 'create' && <BudgetForm budgetTypes={budgetTypes} onSave={handleCreateBudget} onCancel={() => setActiveView('list')} locale={locale} data-id="element-7327" />}

          {activeView === 'edit' && selectedBudget && <BudgetForm budget={selectedBudget} budgetTypes={budgetTypes} onSave={handleUpdateBudget} onCancel={() => {
          setActiveView('list');
          setSelectedBudget(null);
        }} locale={locale} data-id="element-7328" />}

          {activeView === 'types' && <BudgetTypes budgetTypes={budgetTypes} onCreateBudgetType={handleCreateBudgetType} onUpdateBudgetType={handleUpdateBudgetType} onDeleteBudgetType={handleDeleteBudgetType} locale={locale} data-id="element-7329" />}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-7330">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-7331">
            <div className="fixed inset-0 transition-opacity" data-id="element-7332">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-7333"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-7334">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-7335">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-7336">
                <div className="sm:flex sm:items-start" data-id="element-7337">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-7338">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-7339" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-7340">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7341">
                      Delete Budget
                    </h3>
                    <div className="mt-2" data-id="element-7342">
                      <p className="text-sm text-gray-500" data-id="element-7343">
                        Are you sure you want to delete this budget? This action
                        cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-7344">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-7345">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-7346">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}